/** Invitation acceptance page — the reseller sets their own portal password.
 *
 * The admin never chooses this secret: they send a single-use, expiring
 * invitation, and the raw token exists only in the link the reseller opens.
 * Flow: preview (who am I invited as?) -> set password -> go log in.
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { Lock as LockIcon, Visibility, VisibilityOff } from "@mui/icons-material";
import { authApi } from "../../api/client";

interface InvitePreview {
  email: string;
  username: string;
  reseller_account: string;
  expires_at: string;
}

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This invitation link is missing its token.");
      setLoading(false);
      return;
    }
    authApi
      .resellerInvitePreview(token)
      .then((r) => setPreview(r.data))
      .catch((err: any) => {
        const detail = err.response?.data?.detail;
        setError(
          typeof detail === "string" && detail
            ? detail
            : "Invitation not found, already used, or expired."
        );
      })
      .finally(() => setLoading(false));
  }, [token]);

  const mismatch = confirm.length > 0 && confirm !== password;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      await authApi.resellerAcceptInvite(token, password);
      setDone(true);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string" && detail
          ? detail
          : "Could not activate this invitation."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={6}
          sx={{ p: 4, display: "flex", flexDirection: "column", alignItems: "center" }}
        >
          <Box sx={{ bgcolor: "primary.main", borderRadius: "50%", p: 2, mb: 2 }}>
            <LockIcon sx={{ fontSize: 40, color: "white" }} />
          </Box>

          <Typography component="h1" variant="h5" gutterBottom>
            Activate Portal Access
          </Typography>

          {loading ? (
            <CircularProgress sx={{ my: 4 }} />
          ) : done ? (
            <>
              <Alert severity="success" sx={{ width: "100%", mb: 2 }}>
                Portal access activated for <strong>{preview?.username}</strong>.
              </Alert>
              <Button
                component={RouterLink}
                to="/reseller/login"
                variant="contained"
                fullWidth
              >
                Go to Login
              </Button>
            </>
          ) : error ? (
            <>
              <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
                {error}
              </Alert>
              <Button component={RouterLink} to="/reseller/login" fullWidth>
                Back to Login
              </Button>
            </>
          ) : (
            preview && (
              <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You have been invited to the reseller portal for account{" "}
                  <strong>{preview.reseller_account}</strong> as{" "}
                  <strong>{preview.username}</strong> ({preview.email}).
                  Choose a password to activate your access — link valid until{" "}
                  {new Date(preview.expires_at).toLocaleString()}, single use.
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <TextField
                  label="New password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  required
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword((prev) => !prev)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText="At least 10 characters with upper/lower case and a digit"
                />
                <TextField
                  label="Confirm password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  required
                  margin="normal"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  error={mismatch}
                  helperText={mismatch ? "Passwords do not match" : " "}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={busy || mismatch || password.length < 10}
                  sx={{ mt: 2 }}
                >
                  {busy ? <CircularProgress size={24} /> : "Set Password & Activate"}
                </Button>
              </Box>
            )
          )}
        </Paper>
      </Container>
    </Box>
  );
}
